<template>
  <div class="upsell-modal">
    <!-- Product Selection -->
    <SelectInput
      :id="inputs.productId.id"
      :label="inputs.productId.label"
      v-model="inputs.productId.binding"
      :error="inputs.productId.error"
      :rules="inputs.productId.rules"
      :options="availableProductsOptions"
      :placeholder="inputs.productId.placeholder"
      @validate="onValidate('productId', $event)"
    />

    <!-- Product Preview -->
    <div v-if="selectedProduct" class="product-preview">
      <div class="product-preview__image" v-if="selectedProduct.imageUrl">
        <img :src="selectedProduct.imageUrl" :alt="selectedProduct.name" />
      </div>
      <div class="product-preview__info">
        <h4>{{ selectedProduct.name || selectedProduct.title }}</h4>
        <p v-if="selectedProduct.description" class="product-preview__description">
          {{ selectedProduct.description }}
        </p>
        <div class="product-preview__price">
          <strong>€{{ formatPrice(selectedProduct.price || 0) }}</strong>
        </div>
      </div>
    </div>

    <!-- Visibility Settings -->
    <div class="form-toggles">
      <ToggleInput
        id="upsell_show_on_quote"
        label="Toon op offerte"
        v-model="upsellForm.showOnQuote"
      />
      <ToggleInput
        id="upsell_show_on_packing_slip"
        label="Toon op pakbon"
        v-model="upsellForm.showOnPackingSlip"
      />
      <ToggleInput
        id="upsell_show_description_on_quote"
        label="Toon productbeschrijving op offerte"
        v-model="upsellForm.showDescriptionOnQuote"
      />
    </div>

    <!-- Display Settings -->
    <div class="config-section">
      <h4>Weergave instellingen</h4>
      <p class="section-description">
        Bepaal welke informatie wordt getoond bij deze upsell in de configurator.
      </p>
      <div class="form-toggles">
        <ToggleInput
          id="upsell_show_price"
          label="Toon prijs"
          v-model="upsellForm.displaySettings.showPrice"
        />
        <ToggleInput
          id="upsell_show_image"
          label="Toon afbeelding"
          v-model="upsellForm.displaySettings.showImage"
        />
        <ToggleInput
          id="upsell_show_description"
          label="Toon beschrijving"
          v-model="upsellForm.displaySettings.showDescription"
        />
        <ToggleInput
          id="upsell_show_usps"
          label="Toon USPs"
          v-model="upsellForm.displaySettings.showUsps"
        />
      </div>
    </div>

    <!-- Quantity Settings -->
    <div class="config-section">
      <ToggleInput
        id="upsell_allow_quantity"
        label="Sta hoeveelheden toe"
        v-model="upsellForm.allowQuantity"
      />
      <div v-if="upsellForm.allowQuantity" class="quantity-config">
        <h5>Hoeveelheid instellingen</h5>
        <div class="form-row">
          <NumberInput
            id="quantity_min"
            label="Minimum"
            v-model="upsellForm.quantityConfig.min"
            :min="1"
          />
          <NumberInput
            id="quantity_max"
            label="Maximum"
            v-model="upsellForm.quantityConfig.max"
            :min="1"
          />
        </div>
        <NumberInput
          id="quantity_default"
          label="Standaard hoeveelheid"
          v-model="upsellForm.quantityConfig.default"
          :min="upsellForm.quantityConfig.min || 1"
          :max="upsellForm.quantityConfig.max || 999"
        />
      </div>
    </div>

    <!-- Conditional Display -->
    <div class="config-section">
      <h4>Voorwaarden (optioneel)</h4>
      <p class="section-description">
        Bepaal wanneer deze upsell getoond wordt op basis van andere configuratie opties.
      </p>
      <Btn
        variant="secondary"
        @click="openConditionModal"
        type="button"
        :icon="upsellForm.conditionalDisplay ? 'edit' : 'plus'"
      >
        {{ upsellForm.conditionalDisplay ? 'Bewerk voorwaarden' : 'Voeg voorwaarden toe' }}
      </Btn>
      <div v-if="upsellForm.conditionalDisplay" class="condition-summary">
        <div class="condition-summary__type">
          <BaseIcon name="filter" size="16" />
          {{
            upsellForm.conditionalDisplay.type.charAt(0).toUpperCase() +
            upsellForm.conditionalDisplay.type.slice(1)
          }}
          upsell als...
        </div>
        <div class="condition-summary__groups">
          <span
            v-for="(group, groupIndex) in upsellForm.conditionalDisplay.groups"
            :key="groupIndex"
            class="condition-summary__group"
          >
            (
            <template v-for="(rule, ruleIndex) in group.rules" :key="ruleIndex">
              <template v-if="!rule.elementId">
                <span>niet geselecteerd</span>
              </template>
              <template v-else>
                {{
                  availableElements.find((el) => el.id === rule.elementId)?.label || rule.elementId
                }}
                {{ getOperationLabel(rule.operation) }}
                <span v-if="!rule.value">geen</span>
                <span v-else>
                  {{
                    availableElements
                      .find((el) => el.id === rule.elementId)
                      ?.choices?.find((choice) => choice.id === rule.value)?.content || rule.value
                  }}
                </span>
              </template>
              <span v-if="ruleIndex < group.rules.length - 1" class="condition-summary__logic">
                {{ group.logic }}
              </span>
            </template>
            )
            <span
              v-if="groupIndex < upsellForm.conditionalDisplay.groups.length - 1"
              class="condition-summary__logic"
            >
              {{ upsellForm.conditionalDisplay.betweenGroupLogic }}
            </span>
          </span>
        </div>
        <Btn variant="plain" size="sm" @click="clearConditionalDisplay" type="button" icon="trash">
          Verwijder voorwaarden
        </Btn>
      </div>
    </div>

    <!-- Actions -->
    <div class="modal-actions">
      <Btn variant="secondary" @click="handleCancel" type="button">Annuleren</Btn>
      <Btn variant="primary" @click="handleSubmit" :loading="isLoading" type="button">
        {{ isEditing ? 'Opslaan' : 'Toevoegen' }}
      </Btn>
    </div>
  </div>
</template>

<script>
  import { ref, computed, watch } from 'vue';
  import { useUIStore } from '@/store/uiStore';
  import { useFormStore } from '@/store/formStore';
  import TextInput from '@/components/inputs/TextInput.vue';
  import NumberInput from '@/components/inputs/NumberInput.vue';
  import SelectInput from '@/components/inputs/SelectInput.vue';
  import ToggleInput from '@/components/inputs/ToggleInput.vue';
  import Btn from '@/components/partials/Btn.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import ConditionModal from '@/components/form/settings/ConditionModal.vue';
  import { validateInput } from '@/utils/validation';

  export default {
    name: 'UpsellModal',
    components: {
      TextInput,
      NumberInput,
      SelectInput,
      ToggleInput,
      Btn,
      BaseIcon,
    },
    props: {
      upsell: {
        type: Object,
        default: () => null,
      },
      availableProducts: {
        type: Array,
        default: () => [],
        description: 'Alle beschikbare producten voor upsell selectie',
      },
      currentProductId: {
        type: String,
        default: null,
        description: 'ID van het huidige product (om uit te sluiten)',
      },
      productOptions: {
        type: Array,
        default: () => [],
        description: 'Product opties voor conditional display',
      },
    },
    emits: ['submit', 'cancel'],
    setup(props, { emit }) {
      const uiStore = useUIStore();
      const formStore = useFormStore();
      const isLoading = ref(false);
      const isEditing = computed(() => !!props.upsell);

      // Available products for selection (exclude current product)
      const availableProductsOptions = computed(() => {
        return props.availableProducts
          .filter((p) => p.id !== props.currentProductId)
          .map((p) => ({
            value: p.id,
            label: `${p.name || p.title} (€${formatPrice(p.price || 0)})`,
          }));
      });

      // Selected product preview
      const selectedProduct = computed(() => {
        if (!inputs.value.productId.binding) return null;
        return props.availableProducts.find((p) => p.id === inputs.value.productId.binding) || null;
      });

      // Available elements for conditional display (from product options)
      const availableElements = computed(() => {
        return (props.productOptions || []).map((option) => ({
          id: `${props.currentProductId}-${option.id}-input`,
          label: option.label,
          type: option.type,
          choices:
            option.type === 'single-choice' || option.type === 'multi-choice'
              ? option.choiceConfig?.choices || []
              : null,
        }));
      });

      // Form inputs
      const inputs = ref({
        productId: {
          id: 'upsell_product_id',
          label: 'Upsell Product',
          binding: '',
          error: '',
          rules: ['required'],
          placeholder: 'Selecteer een product',
        },
      });

      // Upsell form data
      const upsellForm = ref({
        showOnQuote: true,
        showOnPackingSlip: false,
        showDescriptionOnQuote: false,
        allowQuantity: false,
        quantityConfig: {
          min: 1,
          max: 999,
          default: 1,
        },
        displaySettings: {
          showPrice: true,
          showImage: true,
          showDescription: false,
          showUsps: true,
        },
        conditionalDisplay: null,
      });

      // Watch for upsell changes (when editing)
      watch(
        () => props.upsell,
        (newUpsell) => {
          if (newUpsell) {
            inputs.value.productId.binding = newUpsell.id || newUpsell.productId || '';
            upsellForm.value.showOnQuote = newUpsell.showOnQuote ?? true;
            upsellForm.value.showOnPackingSlip = newUpsell.showOnPackingSlip ?? false;
            upsellForm.value.showDescriptionOnQuote = newUpsell.showDescriptionOnQuote ?? false;
            upsellForm.value.allowQuantity = newUpsell.allowQuantity ?? false;
            upsellForm.value.quantityConfig = {
              min: newUpsell.quantityConfig?.min ?? 1,
              max: newUpsell.quantityConfig?.max ?? 999,
              default: newUpsell.quantityConfig?.default ?? 1,
            };
            upsellForm.value.displaySettings = {
              showPrice: newUpsell.displaySettings?.showPrice ?? true,
              showImage: newUpsell.displaySettings?.showImage ?? true,
              showDescription: newUpsell.displaySettings?.showDescription ?? false,
              showUsps: newUpsell.displaySettings?.showUsps ?? true,
            };
            upsellForm.value.conditionalDisplay = newUpsell.conditionalDisplay || null;
          } else {
            // Reset form for new upsell
            inputs.value.productId.binding = '';
            upsellForm.value = {
              showOnQuote: true,
              showOnPackingSlip: false,
              showDescriptionOnQuote: false,
              allowQuantity: false,
              quantityConfig: {
                min: 1,
                max: 999,
                default: 1,
              },
              displaySettings: {
                showPrice: true,
                showImage: true,
                showDescription: false,
                showUsps: true,
              },
              conditionalDisplay: null,
            };
          }
        },
        { immediate: true }
      );

      // Format price helper
      const formatPrice = (price) => {
        return (price / 100).toFixed(2).replace('.', ',');
      };

      // Get operation label
      const getOperationLabel = (operation) => {
        const labels = {
          equals: 'is',
          not_equals: 'is niet',
          greater_than: 'is groter dan',
          less_than: 'is kleiner dan',
          in: 'is een van',
          not_in: 'is niet een van',
          contains: 'bevat',
        };
        return labels[operation] || operation;
      };

      // Validation
      const onValidate = (field, error) => {
        if (inputs.value[field]) {
          inputs.value[field].error = error;
        }
      };

      // Open condition modal
      const openConditionModal = () => {
        const conditionData = upsellForm.value.conditionalDisplay || {
          type: 'show',
          betweenGroupLogic: 'AND',
          groups: [],
        };

        const modalId = uiStore.addModal(ConditionModal, {
          title: 'Voorwaarden configureren',
          conditions: conditionData,
          items: availableElements.value,
          onSubmit: (updatedConditions) => {
            upsellForm.value.conditionalDisplay = updatedConditions;
            uiStore.closeModal(modalId);
          },
        });
      };

      // Clear conditional display
      const clearConditionalDisplay = () => {
        upsellForm.value.conditionalDisplay = null;
      };

      // Validate form
      const validateForm = () => {
        let isValid = true;

        // Validate productId
        inputs.value.productId.error = validateInput(
          inputs.value.productId.binding,
          inputs.value.productId.rules
        );
        if (inputs.value.productId.error) isValid = false;

        // Validate quantity config if allowQuantity is true
        if (upsellForm.value.allowQuantity) {
          const qtyConfig = upsellForm.value.quantityConfig;
          if (qtyConfig.min > qtyConfig.max) {
            isValid = false;
            // Could add error message here
          }
          if (qtyConfig.default < qtyConfig.min || qtyConfig.default > qtyConfig.max) {
            isValid = false;
            // Could add error message here
          }
        }

        return isValid;
      };

      // Handle submit
      const handleSubmit = () => {
        isLoading.value = true;

        if (!validateForm()) {
          isLoading.value = false;
          return;
        }

        const upsellData = {
          id: inputs.value.productId.binding,
          productId: inputs.value.productId.binding,
          showOnQuote: upsellForm.value.showOnQuote,
          showOnPackingSlip: upsellForm.value.showOnPackingSlip,
          showDescriptionOnQuote: upsellForm.value.showDescriptionOnQuote === true,
          description: selectedProduct.value?.description || '',
          allowQuantity: upsellForm.value.allowQuantity,
          quantityConfig: upsellForm.value.allowQuantity
            ? {
                min: upsellForm.value.quantityConfig.min,
                max: upsellForm.value.quantityConfig.max,
                default: upsellForm.value.quantityConfig.default,
              }
            : undefined,
          displaySettings: {
            showPrice: upsellForm.value.displaySettings.showPrice,
            showImage: upsellForm.value.displaySettings.showImage,
            showDescription: upsellForm.value.displaySettings.showDescription,
            showUsps: upsellForm.value.displaySettings.showUsps,
          },
          conditionalDisplay: upsellForm.value.conditionalDisplay || undefined,
          // Add cached metadata
          name: selectedProduct.value?.name || selectedProduct.value?.title,
          price: selectedProduct.value?.price,
          imageUrl: selectedProduct.value?.imageUrl || selectedProduct.value?.image_url,
        };

        emit('submit', upsellData);
        isLoading.value = false;
      };

      // Handle cancel
      const handleCancel = () => {
        emit('cancel');
      };

      return {
        inputs,
        upsellForm,
        availableProductsOptions,
        selectedProduct,
        availableElements,
        isEditing,
        isLoading,
        formatPrice,
        getOperationLabel,
        onValidate,
        openConditionModal,
        clearConditionalDisplay,
        handleSubmit,
        handleCancel,
      };
    },
  };
</script>

<style scoped lang="scss">
  .upsell-modal {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  .product-preview {
    display: flex;
    gap: 1rem;
    padding: 1rem;
    border: 1px solid var(--slate-200);
    border-radius: 0.5rem;
    background: var(--slate-50);

    &__image {
      width: 80px;
      height: 80px;
      flex-shrink: 0;
      border-radius: 0.25rem;
      overflow: hidden;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }

    &__info {
      flex: 1;

      h4 {
        margin: 0 0 0.5rem 0;
        font-size: 1rem;
        font-weight: 600;
      }

      &__description {
        margin: 0 0 0.5rem 0;
        font-size: 0.875rem;
        color: var(--slate-600);
      }
    }

    &__price {
      font-size: 1.125rem;
      color: var(--brand-600);
    }
  }

  .form-toggles {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .config-section {
    padding: 1rem;
    border: 1px solid var(--slate-200);
    border-radius: 0.5rem;
    background: var(--slate-50);

    h4 {
      margin: 0 0 0.5rem 0;
      font-size: 1rem;
      font-weight: 600;
    }

    h5 {
      margin: 1rem 0 0.5rem 0;
      font-size: 0.875rem;
      font-weight: 600;
      color: var(--slate-700);
    }

    .section-description {
      margin: 0 0 1rem 0;
      font-size: 0.875rem;
      color: var(--slate-600);
    }
  }

  .quantity-config {
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid var(--slate-200);
  }

  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
  }

  .condition-summary {
    margin-top: 1rem;
    padding: 1rem;
    border-radius: 0.5rem;
    background-color: var(--brand-50);
    border: 1px solid var(--brand-500);
    color: var(--slate-600);

    &__type {
      display: flex;
      align-items: center;
      gap: 0.25rem;
      font-weight: 600;
      margin-bottom: 0.5rem;
    }

    &__groups {
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem;
      margin-bottom: 0.5rem;
    }

    &__group {
      font-size: 0.875rem;
    }

    &__logic {
      color: var(--slate-800);
      font-weight: 600;
      margin: 0 0.25rem;
    }
  }

  .modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid var(--slate-200);
  }
</style>
